import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, ListChecks } from 'lucide-react';

// All status codes the system can assign, grouped by area. Line-item codes are
// the 6-char LineItemValidationStatus enum; claim codes are ChargebackStatus.
const GROUPS = [
  {
    name: 'Claim status', hint: 'Where a whole claim sits in the workflow.', tint: 'border-blue-300',
    codes: [
      { code: 'IMPORTED', name: 'Imported', desc: 'New claim ingested from an EDI 844 file, not yet validated.' },
      { code: 'VALIDATED', name: 'Validated', desc: 'Passed every validation check; ready to approve or post.' },
      { code: 'IN_ERROR', name: 'In Error', desc: 'One or more checks failed; the claim needs review.' },
      { code: 'APPROVED', name: 'Approved', desc: 'Adjudicated and accepted; a credit memo can be issued.' },
      { code: 'REJECTED', name: 'Rejected', desc: 'Adjudicated and declined with a reason code (EDI 849 dispute).' },
      { code: 'POSTED', name: 'Posted', desc: 'Settled and posted to the general ledger. Terminal.' },
      { code: 'SUPERSEDED', name: 'Superseded', desc: 'Replaced by a later EDI 844 resubmission (04/05/6R).' },
      { code: 'CANCELLED', name: 'Cancelled', desc: 'Voided by an EDI 844 cancellation (01), or cancelled in processing.' }
    ]
  },
  {
    name: 'Line status', hint: 'The processing state of an individual line item.', tint: 'border-gray-300',
    codes: [
      { code: 'PENDING', name: 'Pending', desc: 'Imported and awaiting validation.' },
      { code: 'VALIDATED', name: 'Validated', desc: 'Line passed all six validation dimensions.' },
      { code: 'MANUALLY_EDITED', name: 'Manually edited', desc: 'A user changed the line (qty/price) after import.' },
      { code: 'ERROR', name: 'Error', desc: 'Line failed validation (see the specific code below).' },
      { code: 'SYSTEM_ERROR', name: 'System error', desc: 'An unexpected error occurred while validating the line.' }
    ]
  },
  {
    name: 'Contract · CNTR', hint: 'Is there a valid, effective contract for this line?', tint: 'border-indigo-300',
    codes: [
      { code: 'CNTRNF', name: 'Contract not found', desc: 'No contract associates this customer with this product.' },
      { code: 'CNTRIN', name: 'Contract inactive', desc: 'A contract exists but is not active.' },
      { code: 'CNTRXP', name: 'Contract expired', desc: 'The contract had already ended on the sale date.' },
      { code: 'CNTRNC', name: 'Not yet effective', desc: 'The contract had not started on the sale date.' },
      { code: 'CNTRDT', name: 'Date mismatch', desc: 'The sale date falls outside the contract’s effective window.' },
      { code: 'CNTRCA', name: 'Customer not on contract', desc: 'The customer isn’t associated with this contract.' },
      { code: 'CNTRNP', name: 'Product not priced', desc: 'The product has no pricing in this contract.' }
    ]
  },
  {
    name: 'Product · PROD', hint: 'Is the product known and eligible?', tint: 'border-emerald-300',
    codes: [
      { code: 'PRODNF', name: 'Product not found', desc: 'The NDC / product code doesn’t match the catalog.' },
      { code: 'PRODIN', name: 'Product inactive', desc: 'The product is inactive or not chargeback-eligible.' },
      { code: 'PRODNDC', name: 'Invalid NDC format', desc: 'The NDC isn’t a valid 10/11-digit code.' },
      { code: 'PRODCS', name: 'Controlled substance', desc: 'Controlled-substance compliance is required for this product.' },
      { code: 'PRODOOS', name: 'Out of stock', desc: 'The product is flagged out of stock.' }
    ]
  },
  {
    name: 'Customer · CUST', hint: 'Is the buying party known and compliant?', tint: 'border-cyan-300',
    codes: [
      { code: 'CUSTNF', name: 'Customer not found', desc: 'The buyer couldn’t be matched to a customer record.' },
      { code: 'CUSTIN', name: 'Customer inactive', desc: 'The customer record is inactive.' },
      { code: 'CUSTNC', name: 'Customer not compliant', desc: 'The customer fails a compliance requirement.' }
    ]
  },
  {
    name: 'Pricing · PRIC', hint: 'Does the claimed price hold up against the contract?', tint: 'border-amber-300',
    codes: [
      { code: 'PRICNF', name: 'Pricing not found', desc: 'No contract price is available for the sale date.' },
      { code: 'PRICVT', name: 'Variance over tolerance', desc: 'The claimed price differs from the contract price beyond the contract’s tolerance.' },
      { code: 'PRICNG', name: 'Negative pricing', desc: 'A price value is negative.' },
      { code: 'PRICUH', name: 'Unusually high price', desc: 'The price is implausibly high versus expectations.' }
    ]
  },
  {
    name: 'Sales data · SALD', hint: 'Does wholesaler sales data corroborate the claim?', tint: 'border-teal-300',
    codes: [
      { code: 'SALDNF', name: 'Sales data not found', desc: 'No matching sales record substantiates the line (warning, not a hard block).' },
      { code: 'SALDPM', name: 'Price mismatch', desc: 'The claimed price doesn’t match the sales record.' },
      { code: 'SALDVV', name: 'Volume variance', desc: 'Claimed quantity is out of line with recent sales volume.' }
    ]
  },
  {
    name: 'Business rules · BUS', hint: 'Required-field and sanity checks.', tint: 'border-orange-300',
    codes: [
      { code: 'BUSDAT', name: 'Invalid date', desc: 'The sale date is in the future or more than a year old.' },
      { code: 'BUSQTY', name: 'Invalid quantity', desc: 'Quantity must be a whole number greater than zero.' },
      { code: 'BUSAMT', name: 'Amount mismatch', desc: 'The line amount doesn’t reconcile with qty × price.' },
      { code: 'BUSCOM', name: 'Compliance not met', desc: 'A required compliance rule isn’t satisfied.' }
    ]
  },
  {
    name: 'Data quality · DATA', hint: 'Completeness and duplicate checks.', tint: 'border-rose-300',
    codes: [
      { code: 'DATADUP', name: 'Duplicate detected', desc: 'The same physical sale (customer + invoice + NDC + date + qty) is being claimed twice.' },
      { code: 'DATAQTY', name: 'Data quality issue', desc: 'The line has a general data-quality problem.' },
      { code: 'DATAMIS', name: 'Missing data', desc: 'A required field is missing from the line.' }
    ]
  }
];

const StatusCodes = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!query) return GROUPS;
    return GROUPS
      .map((g) => {
        const codes = g.codes.filter((c) => (c.code + ' ' + c.name + ' ' + c.desc).toLowerCase().includes(query));
        return codes.length ? { ...g, codes } : null;
      })
      .filter(Boolean);
  }, [query]);

  return (
    <div className="mx-auto max-w-4xl p-6">
      <button onClick={() => navigate('/app/guide')} title="Back to User Guide"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft className="h-4 w-4" /> User Guide
      </button>

      <div className="mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-primary-700 to-indigo-800 p-8 text-white shadow-lg">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-white/15 p-3"><ListChecks className="h-7 w-7" /></div>
          <div>
            <h1 className="text-3xl font-bold">Status Codes</h1>
            <p className="mt-1 text-white/85">Every status a claim or line item can carry, with a short explanation. Search a code or a phrase.</p>
          </div>
        </div>
        <div className="mt-6 flex items-center gap-2 rounded-xl bg-white px-4 py-3 shadow-inner">
          <Search className="h-5 w-5 text-gray-400" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search codes — e.g. “PRICVT”, “tolerance”, “duplicate”, “contract”…"
            className="w-full bg-transparent text-gray-800 placeholder-gray-400 focus:outline-none" />
          {q && <button onClick={() => setQ('')} title="Clear search" className="text-xs font-medium text-gray-400 hover:text-gray-600">Clear</button>}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-gray-500 shadow-sm">No codes match “{q}”.</div>
      ) : (
        <div className="space-y-6">
          {filtered.map((g) => (
            <section key={g.name} className={`overflow-hidden rounded-2xl border-l-4 ${g.tint} bg-white shadow-sm`}>
              <div className="border-b border-gray-100 bg-gray-50/60 px-5 py-3">
                <h2 className="font-semibold text-gray-900">{g.name}</h2>
                <p className="text-xs text-gray-500">{g.hint}</p>
              </div>
              <ul className="divide-y divide-gray-100">
                {g.codes.map((c) => (
                  <li key={c.code + c.name} className="flex flex-col gap-1 px-5 py-3 sm:flex-row sm:items-baseline sm:gap-4">
                    <span className="inline-flex w-fit flex-shrink-0 rounded-md bg-gray-100 px-2 py-0.5 font-mono text-xs font-semibold text-gray-700 sm:w-28">{c.code}</span>
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-gray-900">{c.name}</span>
                      <p className="text-sm text-gray-600">{c.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <p className="mt-6 text-center text-sm text-gray-400">
        Line-item failures also roll up to the claim; see the{' '}
        <button onClick={() => navigate('/app/guide/edi-844-validation')} className="text-primary-600 hover:underline">EDI 844 Validation Pipeline</button>{' '}
        for where each check runs.
      </p>
    </div>
  );
};

export default StatusCodes;
